import { useState, useCallback, useMemo, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchBoatById, cleanBoat } from "store/boats"
import { filterSelectedExperiences } from "../utils/resourceHelpers"

/**
 * Hook to manage equipment selection and CRUD operations
 * @param {Array} equipmentData - All available equipment
 * @returns {Object} Equipment state and handlers
 */
export const useEquipmentManagement = (equipmentData) => {
  const dispatch = useDispatch()
  const [selectedEquipment, setSelectedEquipment] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [boatId, setBoatId] = useState(null)

  // Get boat data from Redux
  const boat = useSelector((state) => state.boats.boat)
  const boatLoading = boat?.loading || false

  // Filter selected equipment data
  const selectedEquipmentData = useMemo(
    () => filterSelectedExperiences(equipmentData, selectedEquipment),
    [equipmentData, selectedEquipment]
  )

  // Fetch boat details when boatId is set and modal is open
  useEffect(() => {
    if (boatId && isAddModalVisible) {
      dispatch(fetchBoatById(boatId))
    }
  }, [boatId, isAddModalVisible, dispatch])

  // Handle equipment edit
  const handleEditEquipment = useCallback((record) => {
    setBoatId(record.id)
    setIsAddModalVisible(true)
  }, [])

  // Handle equipment removal
  const handleRemoveEquipment = useCallback((id) => {
    setSelectedEquipment((prev) => prev.filter((expId) => expId !== String(id)))
  }, [])

  // Handle modal open (for adding new equipment)
  const handleOpenModal = useCallback(() => {
    setBoatId(null)
    setIsAddModalVisible(true)
  }, [])

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsAddModalVisible(false)
    setBoatId(null)
    dispatch(cleanBoat())
  }, [dispatch])

  return {
    selectedEquipment,
    setSelectedEquipment,
    selectedCategories,
    setSelectedCategories,
    selectedEquipmentData,
    isAddModalVisible,
    boat,
    boatId,
    boatLoading,
    handleEditEquipment,
    handleRemoveEquipment,
    handleOpenModal,
    handleCloseModal
  }
}
